import React, { useState, useEffect, useMemo, useRef } from "react";
import api from "../config/api";
import Select from "react-select";
import { toast } from "react-toastify";
import { syncSummary } from "../utils/syncSummary";
import { syncEntity } from "../utils/syncEntity";
import { formatDate } from "../utils/dateUtils";
import { EMPTY_VALUE } from "../utils/display";
import { darkSelectStyles } from "../utils/selectStyles";
import "./Games.css";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/pagination";

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
  next_page: number | null;
  previous_page: number | null;
}

interface GamesResponse {
  data: any[];
  pagination: PaginationInfo;
}

/** The filters the API applies. */
interface GameQuery {
  search: string;
  status: string;
}

const Games: React.FC = () => {
  const [games, setGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Dropdown choices come from the API so they cover every game, not only the
  // ones on the current page.
  const [filterOptions, setFilterOptions] = useState<{ statuses: string[] }>({
    statuses: [],
  });

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const response = await api.get("/api/games/filter-options");
        setFilterOptions(response.data);
      } catch (err) {
        console.error("Error loading game filter options:", err);
      }
    };
    loadFilterOptions();
  }, []);

  // Debounced copy of the search box: it is sent to the API, so it must not fire
  // a request on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoised so the effect below re-runs on a real change, not on every render.
  const activeQuery = useMemo<GameQuery>(
    () => ({ search: debouncedSearch, status: statusFilter }),
    [debouncedSearch, statusFilter],
  );

  // Only the very first load shows the page-level spinner. Later fetches must
  // reload the rows in place: the spinner replaces the whole page, which would
  // unmount the search box and take the focus away mid-typing.
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    fetchGames(currentPage, pageSize, activeQuery, hasLoadedOnce.current);
    hasLoadedOnce.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, activeQuery]);

  /**
   * `query` is matched by the API across every game, not only the rows of the
   * current page.
   *
   * `silent` reloads the rows in place: no page-level spinner, and a failure is
   * reported with a toast instead of replacing the page with the error view.
   */
  const fetchGames = async (
    page: number,
    limit: number,
    query: GameQuery,
    silent = false,
  ) => {
    try {
      if (!silent) setLoading(true);
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (query.search) params.append("search", query.search);
      if (query.status) params.append("status", query.status);

      const response = await api.get<GamesResponse>(
        `/api/games?${params.toString()}`,
      );
      setGames(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      if (silent) {
        toast.error("Erreur lors du rechargement des matchs");
      } else {
        setError("Erreur lors du chargement des matchs");
      }
      console.error("Error fetching games:", err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const syncGames = async () => {
    try {
      setSyncing(true);
      const data = await syncEntity("games");

      if (data.success) {
        // Refresh the rows in place, keeping the active filters
        await fetchGames(currentPage, pageSize, activeQuery, true);
        toast.success(syncSummary(data));
      } else {
        toast.error("Échec de la synchronisation : " + (data.message ?? ""));
      }
    } catch (err) {
      toast.error("Échec de la synchronisation : " + (err as any).message);
      console.error("Sync error:", err);
    } finally {
      setSyncing(false);
    }
  };

  // Any filter change re-queries the whole table, so the current page number is
  // meaningless against the new result set.
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const hasActiveFilters = searchTerm !== "" || statusFilter !== "";

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Chargement des matchs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-error">
        <div className="alert alert-error">{error}</div>
        <button
          className="btn-primary"
          onClick={() => fetchGames(currentPage, pageSize, activeQuery)}
        >
          Réessayer
        </button>
      </div>
    );
  }

  const totalPages = pagination?.total_pages ?? 1;
  const totalItems = pagination?.total_items ?? 0;

  return (
    <div className="games-page">
      <div className="container">
        <div className="page-header">
          <div className="header-content">
            <div>
              <h1 className="page-title">Matchs</h1>
            </div>
            <div className="header-actions">
              <button
                className={`sync-button ${syncing ? "syncing" : ""}`}
                onClick={syncGames}
                disabled={syncing}
              >
                {syncing ? "Synchronisation..." : "Synchroniser Matchs"}
              </button>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group">
              <label>Recherche</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Nom ou ID du match..."
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Statut</label>
              <Select
                value={
                  statusFilter
                    ? { value: statusFilter, label: statusFilter }
                    : null
                }
                onChange={(option) =>
                  handleStatusChange(option ? option.value : "")
                }
                options={[
                  { value: "", label: "Tous les statuts" },
                  ...filterOptions.statuses.map((status) => ({
                    value: status,
                    label: status,
                  })),
                ]}
                styles={darkSelectStyles}
                placeholder="Tous les statuts"
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>

            <div className="filter-group">
              <button
                onClick={handleResetFilters}
                className="btn-secondary reset-filters"
                disabled={!hasActiveFilters}
              >
                Réinitialiser
              </button>
            </div>
          </div>
        </div>

        <div className="page-content">
          <div className="games-table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nom du Match</th>
                  <th>ID Match IA</th>
                  <th>Statut</th>
                  <th>Date de création</th>
                </tr>
              </thead>
              <tbody>
                {games.map((game) => (
                  <tr key={game.id}>
                    <td>{game.id}</td>
                    <td>{game.gameName || EMPTY_VALUE}</td>
                    <td>{game.aia_game_id || EMPTY_VALUE}</td>
                    <td>
                      {game.metadata?.status ? (
                        <span
                          className={`status-badge status-${game.metadata.status
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {game.metadata.status}
                        </span>
                      ) : (
                        EMPTY_VALUE
                      )}
                    </td>
                    <td>{formatDate(game.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-container">
            <div className="pagination-info">
              <span>
                Page {currentPage} sur {totalPages} ({totalItems} matchs au
                total)
              </span>
              <div className="page-size-selector">
                <label>Éléments par page</label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="page-size-select"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                ««
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <span className="pagination-pages">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${
                        currentPage === pageNum ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                »»
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Games;
